import { FC, KeyboardEvent, UIEvent, useEffect, useMemo, useState } from 'react'

import { MagnifyerLineIcon } from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { TextInput } from '@inno-ui/text-input'
import cn from 'clsx'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import { useReleaseArchiveInfiniteQuery } from 'shared/api/productUpdatesApi'
import { ReleaseArchiveItem } from 'shared/api/productUpdatesApi/requests/getArchive/getArchive.types'
import { VideoPlayer } from 'shared/components/VideoPlayer'
import { CustomEvents } from 'shared/constants/customEvents'
import { useDebounce } from 'shared/hooks/useDebounce'
import { listenCustomEvent } from 'shared/utils/customEvents'
import { getDateLibLocale } from 'shared/utils/getDateLibLocale'

import styles from './ReleaseNotesModal.module.scss'

const RELEASE_NOTES_MODAL_Z_INDEX = 1003
const RELEASE_NOTES_MODAL_MAX_WIDTH = 1000
const RELEASE_NOTES_MODAL_MAX_HEIGHT = 680
const SEARCH_DEBOUNCE_MS = 300
const SCROLL_FETCH_THRESHOLD_PX = 120

const formatDate = (date: string, locale: string) =>
  dayjs(date).locale(getDateLibLocale(locale)).format('YYYY-MM-DD')

export const ReleaseNotesModal: FC = () => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'release_notes'
  })

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    return listenCustomEvent(CustomEvents.OPEN_RELEASE_NOTES, () => {
      setIsOpen(true)
    })
  }, [])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useReleaseArchiveInfiniteQuery(debouncedSearch, isOpen)

  const handleClose = () => setIsOpen(false)

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
    }
  }, [isOpen])

  const items = useMemo<ReleaseArchiveItem[]>(
    () => data?.pages.flatMap(page => page?.items ?? []) ?? [],
    [data]
  )

  useEffect(() => {
    if (items.length === 0) return
    const hasSelected = items.some(item => item.id === selectedId)
    if (!hasSelected) {
      setSelectedId(items[0].id)
    }
  }, [items, selectedId])

  const selected = useMemo(
    () => items.find(item => item.id === selectedId) ?? null,
    [items, selectedId]
  )

  const handleItemKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    itemId: string
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setSelectedId(itemId)
    }
  }

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return
    const el = event.currentTarget
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceToBottom < SCROLL_FETCH_THRESHOLD_PX) {
      fetchNextPage()
    }
  }

  const leftSideBar = (
    <div className={styles.sidebar}>
      <TextInput
        value={search}
        onChange={event => setSearch(event.target.value)}
        onClear={() => setSearch('')}
        placeholder={t('searchPlaceholder')}
        icon={MagnifyerLineIcon}
        clearable
      />
      <p className={styles.sidebarLabel}>{t('updates')}</p>
      <div className={styles.list} onScroll={handleListScroll}>
        {items.map(item => (
          <div
            key={item.id}
            role='button'
            tabIndex={0}
            className={cn(styles.listItem, {
              [styles.listItemSelected]: item.id === selectedId
            })}
            onClick={() => setSelectedId(item.id)}
            onKeyDown={event => handleItemKeyDown(event, item.id)}
          >
            <p className={styles.listItemName}>{item.name}</p>
            <p className={styles.listItemDate}>
              {formatDate(item.created_at, i18n.language)}
            </p>
          </div>
        ))}
        {!isLoading && items.length === 0 && (
          <p className={styles.listEmpty}>{t('empty')}</p>
        )}
        {isFetchingNextPage && (
          <p className={styles.loadingMore}>{t('loadingMore')}</p>
        )}
      </div>
    </div>
  )

  const body = selected ? (
    <div className={styles.body}>
      {selected.video_url && (
        <div className={styles.videoHero}>
          <VideoPlayer
            className={styles.videoPlayer}
            videoClassName={styles.video}
            features={{ seek: false }}
            poster={selected.thumbnail_url}
            src={selected.video_url}
            preload='metadata'
            labels={{ video: selected.name }}
          />
        </div>
      )}
      <p className={styles.date}>
        {formatDate(selected.created_at, i18n.language)}
      </p>
      <h2 className={styles.title}>{selected.name}</h2>
      <div className={styles.description}>
        <ReactMarkdown remarkPlugins={[remarkBreaks]}>
          {selected.description}
        </ReactMarkdown>
      </div>
    </div>
  ) : (
    <div className={styles.emptyState}>{t('empty')}</div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      zIndex={RELEASE_NOTES_MODAL_Z_INDEX}
      maxWidth={RELEASE_NOTES_MODAL_MAX_WIDTH}
      maxHeight={RELEASE_NOTES_MODAL_MAX_HEIGHT}
      withHeaderLine
      showCloseButton
      closeOnClickOutside
      leftSideBar={leftSideBar}
      layers={[
        {
          subTitle: t('kicker'),
          title: t('title'),
          bodyPaddings: true,
          isBodyLoading: isLoading,
          body,
          infoLeft: selected
            ? {
                firstLine: `${t('publishedOn')}${formatDate(
                  selected.created_at,
                  i18n.language
                )}`
              }
            : undefined
        }
      ]}
    />
  )
}
